import React, { useState, useRef } from 'react';
import { Phone, Loader, CheckCircle, XCircle, Mic, ArrowRight, Clock, Server, Volume2, Smartphone, Play, Code, AlertCircle } from 'lucide-react';

interface Step {
  id: string;
  title: string;
  description: string;
  status: 'waiting' | 'active' | 'completed' | 'error';
  icon: React.ReactNode;
  detail?: string;
  apiResponse?: any;
}

interface ApiLog {
  endpoint: string;
  request?: any;
  response?: any;
  error?: string;
  timestamp: string;
}

export default function VoicePoC() {
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('+15105026303');
  const [testMessage, setTestMessage] = useState('Hello! This is a test call from Future Bet. If you hear this message, the voice system is working perfectly!');
  const [apiLogs, setApiLogs] = useState<ApiLog[]>([]);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [showApiDetails, setShowApiDetails] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  const [steps, setSteps] = useState<Step[]>([
    {
      id: 'validate',
      title: 'Validate Input',
      description: 'Checking phone number format',
      status: 'waiting',
      icon: <CheckCircle className="w-5 h-5" />
    },
    {
      id: 'tts',
      title: 'Generate Voice',
      description: 'Converting text to speech with ElevenLabs',
      status: 'waiting',
      icon: <Volume2 className="w-5 h-5" />
    },
    {
      id: 'upload',
      title: 'Upload Audio',
      description: 'Storing audio file in cloud',
      status: 'waiting',
      icon: <Server className="w-5 h-5" />
    },
    {
      id: 'call',
      title: 'Initiate Call',
      description: 'Connecting via Twilio',
      status: 'waiting',
      icon: <Phone className="w-5 h-5" />
    },
    {
      id: 'ring',
      title: 'Ringing',
      description: 'Your phone should be ringing',
      status: 'waiting',
      icon: <Smartphone className="w-5 h-5" />
    }
  ]);

  const updateStep = (stepId: string, updates: Partial<Step>) => {
    setSteps(prev => prev.map(step =>
      step.id === stepId ? { ...step, ...updates } : step
    ));
  };

  const resetSteps = () => {
    setSteps(prev => prev.map(step => ({
      ...step,
      status: 'waiting',
      detail: undefined,
      apiResponse: undefined
    })));
    setApiLogs([]);
    setAudioUrl(null);
  };

  const logApi = (endpoint: string, request?: any, response?: any, error?: string) => {
    const log: ApiLog = {
      endpoint,
      request,
      response,
      error,
      timestamp: new Date().toISOString()
    };
    setApiLogs(prev => [...prev, log]);
    console.log('API Log:', log);
  };

  const testAudioPreview = async () => {
    try {
      logApi('/voice/audio', { message: testMessage, voiceId: 'EXAVITQu4vr4xnSDxMaL' }, null);

      const response = await fetch('https://futr-bet-api.b-9f2.workers.dev/voice/audio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: testMessage,
          voiceId: 'EXAVITQu4vr4xnSDxMaL'
        })
      });

      const responseInfo = {
        status: response.status,
        statusText: response.statusText,
        headers: {
          contentType: response.headers.get('content-type'),
          contentLength: response.headers.get('content-length')
        }
      };

      if (response.ok && response.headers.get('content-type')?.includes('audio')) {
        const blob = await response.blob();
        responseInfo['audioSize'] = blob.size;
        responseInfo['audioType'] = blob.type;

        logApi('/voice/audio', null, {
          success: true,
          ...responseInfo,
          message: 'ElevenLabs audio generated successfully'
        });

        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        if (audioRef.current) {
          audioRef.current.src = url;
          audioRef.current.play();
        }
      } else {
        const errorText = await response.text();
        logApi('/voice/audio', null, {
          success: false,
          ...responseInfo,
          error: errorText
        });
        alert(`ElevenLabs API Response:\n\nStatus: ${response.status}\nContent-Type: ${response.headers.get('content-type')}\n\n${errorText}`);
      }
    } catch (error: any) {
      logApi('/voice/audio', null, null, error.message);
      console.error('Audio preview error:', error);
      alert(`Error calling ElevenLabs:\n${error.message}`);
    }
  };

  const makeTestCall = async () => {
    resetSteps();
    setLoading(true);

    // Step 1: Validate
    updateStep('validate', { status: 'active' });
    await new Promise(r => setTimeout(r, 500));

    if (!phoneNumber || !phoneNumber.startsWith('+')) {
      updateStep('validate', {
        status: 'error',
        detail: 'Phone number must include country code (e.g., +1)'
      });
      setLoading(false);
      return;
    }

    updateStep('validate', {
      status: 'completed',
      detail: `Phone: ${phoneNumber}`
    });

    // Step 2: Generate TTS
    updateStep('tts', { status: 'active' });
    await new Promise(r => setTimeout(r, 1500));

    try {
      // Generate audio with ElevenLabs
      const audioRequest = {
        message: testMessage,
        voiceId: 'EXAVITQu4vr4xnSDxMaL' // Bella voice
      };

      updateStep('tts', {
        status: 'active',
        detail: 'Calling ElevenLabs API...'
      });

      const audioResponse = await fetch('https://futr-bet-api.b-9f2.workers.dev/voice/audio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(audioRequest)
      });

      let audioUrl = null;
      let audioData = null;

      if (audioResponse.ok) {
        // Successfully got audio
        const blob = await audioResponse.blob();
        audioUrl = URL.createObjectURL(blob);
        setAudioUrl(audioUrl);

        logApi('/voice/audio', audioRequest, {
          status: 'success',
          contentType: audioResponse.headers.get('content-type'),
          size: blob.size
        });

        updateStep('tts', {
          status: 'completed',
          detail: `✓ ElevenLabs AI Voice Generated (${(blob.size / 1024).toFixed(1)}KB)`,
          apiResponse: { success: true, size: blob.size }
        });
      } else {
        // Failed, log the error
        const errorText = await audioResponse.text();
        logApi('/voice/audio', audioRequest, null, errorText);

        updateStep('tts', {
          status: 'error',
          detail: 'ElevenLabs failed, using fallback voice',
          apiResponse: { error: errorText }
        });
        // Don't throw, continue with fallback
      }

      // Step 3: Prepare TwiML
      updateStep('upload', {
        status: 'active',
        detail: 'Creating TwiML configuration...'
      });
      await new Promise(r => setTimeout(r, 800));

      const twimlUrl = `https://futr-bet-api.b-9f2.workers.dev/voice/twiml?message=${encodeURIComponent(testMessage)}`;

      updateStep('upload', {
        status: 'completed',
        detail: audioUrl ? '✓ Using ElevenLabs AI Voice' : '✓ Using Twilio Fallback Voice',
        apiResponse: { twimlUrl, hasAudio: !!audioUrl }
      });

      // Step 4: Initiate Call
      updateStep('call', { status: 'active' });
      await new Promise(r => setTimeout(r, 1200));

      const callRequest = {
        to: phoneNumber,
        message: testMessage
      };

      const callResponse = await fetch('https://futr-bet-api.b-9f2.workers.dev/voice/poc-call', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(callRequest)
      });

      const callData = await callResponse.json();
      logApi('/voice/poc-call', callRequest, callData);

      if (!callData.success) {
        throw new Error(callData.error || callData.twilioError || 'Call initiation failed');
      }

      updateStep('call', {
        status: 'completed',
        detail: `✓ Twilio Call SID: ${callData.callSid?.substring(0, 20) || 'N/A'}...`,
        apiResponse: callData
      });

      // Step 5: Ringing
      updateStep('ring', {
        status: 'active',
        detail: 'Calling your phone...'
      });
      await new Promise(r => setTimeout(r, 3000));
      updateStep('ring', {
        status: 'completed',
        detail: '✓ Call completed successfully!'
      });

    } catch (error: any) {
      logApi('error', null, null, error.message);
      setSteps(prev => prev.map(step => ({
        ...step,
        status: step.status === 'waiting' ? 'error' : step.status,
        detail: step.status === 'active' ? `❌ ${error.message}` : step.detail,
        apiResponse: step.status === 'active' ? { error: error.message } : step.apiResponse
      })));
    } finally {
      setLoading(false);
    }
  };

  const getStepColor = (status: string) => {
    switch(status) {
      case 'completed': return 'text-green-400 bg-green-900/20 border-green-600';
      case 'active': return 'text-blue-400 bg-blue-900/20 border-blue-600 animate-pulse';
      case 'error': return 'text-red-400 bg-red-900/20 border-red-600';
      default: return 'text-gray-500 bg-gray-800/50 border-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="container mx-auto px-4 py-8 max-w-4xl">

        {/* Header - NO PURPLE */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <Phone className="w-20 h-20 text-blue-400" />
              <Mic className="w-10 h-10 text-green-400 absolute -bottom-2 -right-2" />
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-2 text-white">
            Voice Call PoC
          </h1>
          <p className="text-gray-400 text-lg">
            Watch every step as we make a voice call
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Input Form */}
          <div className="bg-gray-900 rounded-xl p-6 border border-gray-700">
            <h2 className="text-xl font-bold mb-6 text-white">Configuration</h2>

            {/* Phone Number */}
            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                placeholder="+1234567890"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="w-full bg-black border border-gray-600 rounded-lg px-4 py-3 text-white focus:border-blue-400 focus:outline-none"
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Include country code (e.g., +1 for US)
              </p>
            </div>

            {/* Message */}
            <div className="mb-6">
              <label className="block text-sm text-gray-400 mb-2">
                Message to Speak
              </label>
              <textarea
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                className="w-full bg-black border border-gray-600 rounded-lg px-4 py-3 text-white focus:border-blue-400 focus:outline-none resize-none"
                rows={4}
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                {testMessage.length} characters
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={makeTestCall}
                disabled={loading || !phoneNumber}
                className={`
                  w-full py-4 rounded-lg font-semibold text-lg transition-all
                  ${loading || !phoneNumber
                    ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-500 text-white transform hover:scale-105'
                  }
                `}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-3">
                    <Loader className="w-5 h-5 animate-spin" />
                    Processing...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-3">
                    <Phone className="w-5 h-5" />
                    Make Test Call
                  </span>
                )}
              </button>

              <div className="flex gap-3">
                <button
                  onClick={testAudioPreview}
                  disabled={loading || !testMessage}
                  className="flex-1 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="flex items-center justify-center gap-2">
                    <Play className="w-4 h-4" />
                    Preview Audio
                  </span>
                </button>

                <button
                  onClick={() => setShowApiDetails(!showApiDetails)}
                  className="flex-1 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-medium transition-all"
                >
                  <span className="flex items-center justify-center gap-2">
                    <Code className="w-4 h-4" />
                    {showApiDetails ? 'Hide' : 'Show'} API Details
                  </span>
                </button>
              </div>
            </div>

            {/* Hidden Audio Element */}
            <audio ref={audioRef} className="hidden" />

            {/* Audio Preview Status */}
            {audioUrl && (
              <div className="mt-4 p-3 bg-green-900/20 border border-green-600/50 rounded-lg">
                <p className="text-sm text-green-400 flex items-center gap-2">
                  <Volume2 className="w-4 h-4" />
                  ✅ Audio file generated successfully!
                </p>

                {/* Audio Player */}
                <div className="mt-3">
                  <audio
                    controls
                    className="w-full"
                    src={audioUrl}
                    style={{ background: 'transparent' }}
                  >
                    Your browser does not support the audio element.
                  </audio>
                </div>

                {/* Download Link */}
                <div className="mt-2 flex items-center gap-3">
                  <a
                    href={audioUrl}
                    download="futrbet-voice.mp3"
                    className="text-xs text-blue-400 hover:text-blue-300 underline flex items-center gap-1"
                  >
                    📥 Download Audio File
                  </a>
                  <span className="text-xs text-gray-500">
                    {audioUrl.startsWith('blob:') ? 'Local file (browser generated)' : 'Server generated'}
                  </span>
                </div>
              </div>
            )}

            {/* Test ElevenLabs Directly */}
            <div className="mt-4 p-4 bg-gray-800 rounded-lg border border-gray-600">
              <h3 className="text-sm font-semibold mb-2 text-white">Quick ElevenLabs Test</h3>
              <button
                onClick={async () => {
                  const testResponse = await fetch('https://futr-bet-api.b-9f2.workers.dev/voice/audio', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      message: 'Testing. One, two, three.',
                      voiceId: 'EXAVITQu4vr4xnSDxMaL'
                    })
                  });

                  const info = {
                    status: testResponse.status,
                    contentType: testResponse.headers.get('content-type'),
                    isAudio: testResponse.headers.get('content-type')?.includes('audio')
                  };

                  if (info.isAudio) {
                    const blob = await testResponse.blob();
                    info['audioSize'] = `${(blob.size / 1024).toFixed(1)} KB`;
                  }

                  alert(`ElevenLabs Direct Test:\n\n${JSON.stringify(info, null, 2)}`);
                }}
                className="w-full py-2 bg-yellow-600 hover:bg-yellow-500 text-black rounded font-medium transition-all"
              >
                🧪 Test ElevenLabs API Directly
              </button>
            </div>
          </div>

          {/* Right: Step Progress */}
          <div className="bg-gray-900 rounded-xl p-6 border border-gray-700">
            <h2 className="text-xl font-bold mb-6 text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-400" />
              Process Steps
            </h2>

            <div className="space-y-3">
              {steps.map((step, index) => (
                <div key={step.id}>
                  <div className={`rounded-lg border p-4 transition-all ${getStepColor(step.status)}`}>
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 ${step.status === 'active' ? 'animate-pulse' : ''}`}>
                        {step.icon}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold">{step.title}</div>
                        <div className="text-sm opacity-75">{step.description}</div>
                        {step.detail && (
                          <div className="text-xs mt-1 font-mono opacity-60">
                            {step.detail}
                          </div>
                        )}
                      </div>
                      <div>
                        {step.status === 'completed' && <CheckCircle className="w-5 h-5" />}
                        {step.status === 'error' && <XCircle className="w-5 h-5" />}
                        {step.status === 'active' && <Loader className="w-5 h-5 animate-spin" />}
                      </div>
                    </div>
                  </div>

                  {index < steps.length - 1 && (
                    <div className="flex justify-center py-1">
                      <ArrowRight className={`w-4 h-4 ${
                        step.status === 'completed' ? 'text-green-400' : 'text-gray-600'
                      }`} />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Status Summary */}
            <div className="mt-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
              <div className="text-sm text-gray-400">
                {loading ? (
                  <span className="text-blue-400">Call in progress...</span>
                ) : steps.every(s => s.status === 'completed') ? (
                  <span className="text-green-400">✓ Call simulation completed successfully</span>
                ) : steps.some(s => s.status === 'error') ? (
                  <span className="text-red-400">✗ Call failed - check error above</span>
                ) : (
                  <span>Ready to make a test call</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* API Details Section */}
        {showApiDetails && apiLogs.length > 0 && (
          <div className="mt-8 bg-gray-900 rounded-xl p-6 border border-gray-700">
            <h3 className="text-lg font-bold mb-4 text-white flex items-center gap-2">
              <Code className="w-5 h-5" />
              API Call Details
            </h3>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {apiLogs.map((log, index) => (
                <div key={index} className="bg-black rounded-lg p-4 border border-gray-600">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-blue-400">{log.endpoint}</span>
                    <span className="text-xs text-gray-500">
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </span>
                  </div>

                  {log.request && (
                    <div className="mb-2">
                      <div className="text-xs text-gray-400 mb-1">Request:</div>
                      <pre className="text-xs bg-gray-900 p-2 rounded overflow-x-auto">
                        {JSON.stringify(log.request, null, 2)}
                      </pre>
                    </div>
                  )}

                  {log.response && (
                    <div className="mb-2">
                      <div className="text-xs text-gray-400 mb-1">Response:</div>
                      <pre className="text-xs bg-gray-900 p-2 rounded overflow-x-auto">
                        {JSON.stringify(log.response, null, 2)}
                      </pre>
                    </div>
                  )}

                  {log.error && (
                    <div className="text-xs text-red-400 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {log.error}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* System Status */}
        <div className="mt-8 p-6 bg-gray-900 border border-gray-700 rounded-lg">
          <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
            <Server className="w-5 h-5" />
            System Status
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span className="text-gray-400">Twilio API: </span>
              <span className="text-green-400">Connected</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span className="text-gray-400">ElevenLabs API: </span>
              <span className="text-green-400">Connected</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
              <span className="text-gray-400">Voice Mode: </span>
              <span className="text-blue-400">AI Voice with Fallback</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
              <span className="text-gray-400">Environment: </span>
              <span className="text-yellow-400">Production</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}